import { api, apiBaseUrl } from '../api';
import { getDriverSession } from './driverSession';

/**
 * The rider's position with the phone locked or the app in the background
 * (the mobile plan's M6; InGo ADR 0028's sibling): `@capgo/background-geolocation`
 * runs a foreground service of type location, started from the rider's own tap
 * on "Go online", and posts each fix natively to InGo, so it does not depend
 * on the WebView, which Android pauses minutes after the app leaves the screen.
 *
 * Two tiers, as the plan sets them:
 * - **on duty**: to `rider/me/presence` about once a minute, so dispatch finds
 *   the rider;
 * - **carrying a job**: to `journeys/delivery/{id}/locations` every few seconds, what
 *   the client's map follows and the distance is measured from.
 *
 * It posts with a token that can do nothing else (`POST rider/me/location-token`,
 * revoked off duty): the plugin keeps its headers in the app's plain storage so
 * a restarted service can still authenticate, and the rider's own session token
 * stays in the keystore.
 *
 * The web build and a phone without the plugin do nothing here; the app's own
 * pings cover the open screen (`useJourneyPings`). Every start, switch and stop
 * says so in the console, so "the client's map froze" has a line to read.
 */

// Half the cadence wanted, on purpose (measured on the emulator, 2 Oct 2026).
// The plugin uses one number for two things: how often it asks the GPS for a
// fix and the least time between two posts, timed by the fixes themselves.
// Android may deliver a fix up to a tenth early, the post rule then drops it,
// and the next comes a whole interval later: at 60 s a rider on duty posted
// every two minutes, and the plugin's own 60 s watchdog restarted the GPS
// between. At half, a post goes every one to two intervals: duty within the
// server's 60 s heartbeat, a job within 6 s.
export const DUTY_INTERVAL_MS = 30000;
export const JOB_INTERVAL_MS = 3000;

let token = null;
let tokenUntil = 0;

// The location token outlives a page reload (2 Oct 2026: each reload asked
// for a new one, ten in a minute against a limit of ten). It can only post
// positions (ADR 0031) and the plugin already keeps it in plain storage, so
// keeping it here exposes nothing new. Kept with the rider it belongs to, so a
// phone handed to another rider never posts as the first; used only while
// more than two hours of its life remain, so a long shift gets a fresh one at
// its next start or switch rather than a refused one.
const TOKEN_KEY = 'ingo_location_token';
const RENEW_BEFORE_MS = 2 * 60 * 60 * 1000;

function riderId() {
  return String(getDriverSession()?.id ?? '');
}

function keptToken(rider) {
  try {
    const kept = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null');
    if (!rider || !kept || kept.rider !== rider || typeof kept.token !== 'string') return null;

    return Number(kept.until) - Date.now() > RENEW_BEFORE_MS ? { token: kept.token, until: Number(kept.until) } : null;
  } catch {
    return null;
  }
}

function keepToken(rider, value, until) {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ rider, token: value, until }));
  } catch {
    // Private mode: asked for again after a reload, as before.
  }
}

function forgetToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing kept.
  }
}

/**
 * The rider's location token while it has life left: the one in hand, else
 * the one kept from before a reload, else a new one from the server.
 */
async function locationToken() {
  if (token && tokenUntil - Date.now() > RENEW_BEFORE_MS) return token;
  const rider = riderId();
  const kept = keptToken(rider);
  if (kept) {
    token = kept.token;
    tokenUntil = kept.until;

    return token;
  }
  const { data } = await api.post('/rider/me/location-token', {});
  if (!data?.token) throw new Error('No location token');
  token = data.token;
  tokenUntil = Date.now() + (Number(data.hours) > 0 ? Number(data.hours) : 16) * 60 * 60 * 1000;
  keepToken(rider, token, tokenUntil);

  return token;
}
let running = null; // { stage: 'duty' | 'job', deliveryId: string | null }
/** The job last said to be carried, kept even before recording starts (an app reopened mid-job). */
let carried = null;
let pending = Promise.resolve();

function plugin() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    if (!cap?.isNativePlatform?.()) return null;
    return cap.Plugins?.BackgroundGeolocation ?? null;
  } catch {
    return null;
  }
}

/** Whether this phone can record its position natively (the plugin is in the build). */
export function recordsNatively() {
  return plugin() !== null;
}

/**
 * Whether native recording is running now, or, given a job, posting that job's
 * positions: the app's own pings stand down only then. A recorder left on the
 * duty tier (a switch refused) must not silence both.
 */
export function nativeRunning(deliveryId) {
  if (running === null) return false;

  return deliveryId == null || (running.stage === 'job' && running.deliveryId === String(deliveryId));
}

const DISCLOSED_KEY = 'ingo_positions_disclosed';

/** Whether the rider has read, on this phone, that their location is shared while online (Play's disclosure). */
export function positionsDisclosed() {
  try {
    return localStorage.getItem(DISCLOSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markPositionsDisclosed() {
  try {
    localStorage.setItem(DISCLOSED_KEY, '1');
  } catch {
    // Private mode: asked again next time.
  }
}

function urlFor(stage, deliveryId) {
  const base = apiBaseUrl().replace(/\/$/, '');

  return stage === 'job' ? `${base}/journeys/delivery/${encodeURIComponent(deliveryId)}/locations` : `${base}/rider/me/presence`;
}

/** One change at a time: a stop and a start never interleave. */
function serially(step) {
  pending = pending.then(step, step);

  return pending;
}

async function startPlugin(stage, deliveryId) {
  const bg = plugin();
  if (!bg) return false;
  token = await locationToken();
  if (running) await bg.stop().catch(() => {});
  await bg.start(
    {
      backgroundTitle: 'InGo: you are online',
      backgroundMessage: stage === 'job' ? 'Sharing your position with your client while you deliver.' : 'Sharing your position so jobs near you reach you.',
      requestPermissions: true,
      stale: false,
      distanceFilter: 0,
      url: urlFor(stage, deliveryId),
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      minIntervalMs: stage === 'job' ? JOB_INTERVAL_MS : DUTY_INTERVAL_MS,
    },
    // The fixes also arrive here while the WebView is awake; the native post is the record.
    () => {},
  );
  running = { stage, deliveryId: stage === 'job' ? String(deliveryId) : null };
  console.info(`[positions] native recording ${JSON.stringify(running)}`);

  return true;
}

/** On duty, by the rider's own tap: the slow tier, or the job's if one is already carried. */
export function startOnDuty() {
  return serially(async () => {
    if (running) return true;
    try {
      return await startPlugin(carried ? 'job' : 'duty', carried);
    } catch (e) {
      console.warn(`[positions] could not start on duty ${JSON.stringify({ message: e?.message || String(e) })}`);
      return false;
    }
  });
}

/** Carrying a job, or back to the slow tier when `deliveryId` is null. */
export function followJob(deliveryId) {
  carried = deliveryId ? String(deliveryId) : null;
  return serially(async () => {
    if (!running) return false; // Never started from a push or a poll: only the rider's tap starts it.
    const wanted = carried;
    if ((running.deliveryId ?? null) === wanted) return true;
    try {
      return await startPlugin(wanted ? 'job' : 'duty', wanted);
    } catch (e) {
      console.warn(`[positions] could not switch ${JSON.stringify({ to: wanted, message: e?.message || String(e) })}`);
      return false;
    }
  });
}

/** Off duty or signed out: the service stops and the token goes. */
export function stopRecording() {
  return serially(async () => {
    const bg = plugin();
    if (bg && running) await bg.stop().catch(() => {});
    // Any kept token, whoever's: at sign-out the session may already be gone.
    let had = token !== null;
    try {
      had = had || localStorage.getItem(TOKEN_KEY) !== null;
    } catch {
      // Nothing readable is nothing kept.
    }
    running = null;
    token = null;
    forgetToken();
    carried = null;
    if (had) await api.delete('/rider/me/location-token').catch(() => {});
    if (bg) console.info('[positions] native recording stopped');

    return true;
  });
}
